import { useState } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuSeparator, DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel,
  AlertDialogContent, AlertDialogDescription, AlertDialogFooter,
  AlertDialogHeader, AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { UserPlus, MoreHorizontal, ShieldAlert, ShieldCheck, Pencil, Ban, RotateCcw, Key } from 'lucide-react';
import { useTeamMembers, useDeactivateTeamMember, type TeamMember } from '@/hooks/useTeamManagement';
import { usePermissions } from '@/hooks/usePermissions';
import { useAuth } from '@/contexts/AuthContext';
import { useGovernedAction } from '@/hooks/useGovernedAction';
import { SERVICE_KEYS } from '@/lib/serviceKeys';
import { InviteUserDialog } from './InviteUserDialog';
import { EditUserDialog } from './EditUserDialog';
import { PermissionEditor } from './PermissionEditor';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { ShieldOff } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { formatLocalizedDate } from '@/lib/localizedDate';

export function UsersList() {
  const { t, i18n } = useTranslation();
  const { data: members, isLoading } = useTeamMembers();
  const { can } = usePermissions();
  const { user } = useAuth();
  const deactivate = useDeactivateTeamMember();
  const teamGov = useGovernedAction(SERVICE_KEYS.TEAM_MANAGEMENT);

  const [inviteOpen, setInviteOpen] = useState(false);
  const [editMember, setEditMember] = useState<TeamMember | null>(null);
  const [permMember, setPermMember] = useState<TeamMember | null>(null);
  const [confirmAction, setConfirmAction] = useState<{ member: TeamMember; action: 'deactivate' | 'reactivate' } | null>(null);

  const canManage = can('users', 'manage') || can('users', 'edit');
  const canInvite = can('users', 'invite') || can('users', 'create');

  const statusColor = (status: string) => {
    switch (status) {
      case 'active': return 'bg-success/10 text-success border-success/20';
      case 'deactivated': return 'bg-destructive/10 text-destructive border-destructive/20';
      case 'invited': return 'bg-info/10 text-info border-info/20';
      default: return 'bg-muted text-muted-foreground';
    }
  };

  const statusLabel = (status: string) => {
    const knownStatuses: Record<string, string> = {
      active: t('hire.team.usersList.statuses.active'),
      deactivated: t('hire.team.usersList.statuses.deactivated'),
      invited: t('hire.team.usersList.statuses.invited'),
    };

    return knownStatuses[status] || status;
  };

  if (isLoading) {
    return (
      <Card className="p-6 rounded-xl">
        <div className="space-y-3">
          {[1, 2, 3].map(i => <Skeleton key={i} className="h-16 w-full rounded-xl" />)}
        </div>
      </Card>
    );
  }

  return (
    <>
      <Card className="rounded-xl shadow-card border-border">
        {teamGov.shouldDisable && (
          <Alert variant="destructive" className="m-4 mb-0">
            <ShieldOff className="h-4 w-4" />
            <AlertDescription>{teamGov.message || t('hire.team.usersList.unavailable')}</AlertDescription>
          </Alert>
        )}
        <div className="flex items-center justify-between p-5 border-b border-border">
          <h2 className="font-display font-semibold text-foreground text-lg">
            {t('hire.team.usersList.title', { count: members?.length || 0 })}
          </h2>
          {canInvite && (
            <Button
              className="bg-primary text-primary-foreground hover:bg-primary/90 rounded-xl gap-1.5"
              onClick={teamGov.guardedAction(() => setInviteOpen(true))}
              disabled={teamGov.shouldDisable}
            >
              <UserPlus className="w-4 h-4" /> {t('hire.team.usersList.inviteMember')}
            </Button>
          )}
        </div>

        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{t('hire.team.usersList.columns.name')}</TableHead>
                <TableHead>{t('hire.team.usersList.columns.role')}</TableHead>
                <TableHead className="hidden md:table-cell">{t('hire.team.usersList.columns.department')}</TableHead>
                <TableHead>{t('hire.team.usersList.columns.status')}</TableHead>
                <TableHead className="hidden lg:table-cell">{t('hire.team.usersList.columns.joined')}</TableHead>
                {canManage && <TableHead className="w-12">{t('hire.team.usersList.columns.actions')}</TableHead>}
              </TableRow>
            </TableHeader>
            <TableBody>
              {!members?.length ? (
                <TableRow>
                  <TableCell colSpan={6} className="text-center text-muted-foreground py-10">
                    {t('hire.team.usersList.empty')}
                  </TableCell>
                </TableRow>
              ) : (
                members.map((m) => (
                  <TableRow key={m.user_id}>
                    <TableCell>
                      <div className="flex items-center gap-3">
                        <div className="w-9 h-9 rounded-full bg-primary flex items-center justify-center text-primary-foreground text-xs font-bold flex-shrink-0">
                          {m.full_name?.split(' ').map(n => n[0]).join('').slice(0, 2) || '?'}
                        </div>
                        <div>
                          <p className="text-sm font-medium text-foreground">{m.full_name}</p>
                          <p className="text-xs text-muted-foreground">{m.email}</p>
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1.5">
                        {(m.role_definition_slug === 'org_owner' || m.role_definition_slug === 'org_admin' || m.role === 'org_owner' || m.role === 'org_admin')
                          ? <ShieldAlert className="w-3.5 h-3.5 text-accent" />
                          : <ShieldCheck className="w-3.5 h-3.5 text-muted-foreground" />
                        }
                        <span className="text-sm">{m.role_definition_name || (m.role === 'org_owner' ? t('hire.team.usersList.organizationOwner') : m.role)}</span>
                      </div>
                    </TableCell>
                    <TableCell className="hidden md:table-cell">
                      <span className="text-sm text-muted-foreground">{m.department || '—'}</span>
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline" className={`text-xs capitalize ${statusColor(m.status)}`}>
                        {statusLabel(m.status)}
                      </Badge>
                    </TableCell>
                    <TableCell className="hidden lg:table-cell">
                      <span className="text-xs text-muted-foreground">
                        {formatLocalizedDate(m.created_at, 'MMM d, yyyy', i18n.language)}
                      </span>
                    </TableCell>
                    {canManage && (
                      <TableCell>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="icon" className="h-8 w-8">
                              <MoreHorizontal className="w-4 h-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end" className="rounded-xl">
                            {!(m.role_definition_slug === 'org_owner' || m.role_definition_slug === 'org_admin' || m.role === 'org_owner' || m.role === 'org_admin') && (
                              <DropdownMenuItem
                                onClick={teamGov.guardedAction(() => setEditMember(m))}
                                className="gap-2"
                                disabled={teamGov.shouldDisable}
                              >
                                <Pencil className="w-3.5 h-3.5" /> {t('hire.team.usersList.actions.editUser')}
                              </DropdownMenuItem>
                            )}
                            <DropdownMenuItem
                              onClick={teamGov.guardedAction(() => setPermMember(m))}
                              className="gap-2"
                              disabled={teamGov.shouldDisable && !(m.role_definition_slug === 'org_owner' || m.role_definition_slug === 'org_admin' || m.role === 'org_owner' || m.role === 'org_admin')}
                            >
                              <Key className="w-3.5 h-3.5" />
                              {(m.role_definition_slug === 'org_owner' || m.role_definition_slug === 'org_admin' || m.role === 'org_owner' || m.role === 'org_admin')
                                ? t('hire.team.usersList.actions.viewPermissions')
                                : t('hire.team.usersList.actions.customizePermissions')
                              }
                            </DropdownMenuItem>
                            <DropdownMenuSeparator />
                            {m.status === 'active' && m.user_id !== user?.id && (
                              <DropdownMenuItem
                                onClick={teamGov.guardedAction(() => setConfirmAction({ member: m, action: 'deactivate' }))}
                                className="gap-2 text-destructive focus:text-destructive"
                                disabled={teamGov.shouldDisable}
                              >
                                <Ban className="w-3.5 h-3.5" /> {t('hire.team.usersList.actions.deactivate')}
                              </DropdownMenuItem>
                            )}
                            {m.status === 'deactivated' && (
                              <DropdownMenuItem
                                onClick={teamGov.guardedAction(() => setConfirmAction({ member: m, action: 'reactivate' }))}
                                className="gap-2 text-success"
                                disabled={teamGov.shouldDisable}
                              >
                                <RotateCcw className="w-3.5 h-3.5" /> {t('hire.team.usersList.actions.reactivate')}
                              </DropdownMenuItem>
                            )}
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>
                    )}
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>
      </Card>

      <InviteUserDialog open={inviteOpen} onOpenChange={setInviteOpen} />

      {editMember && (
        <EditUserDialog
          member={editMember}
          open={!!editMember}
          onOpenChange={(open) => { if (!open) setEditMember(null); }}
        />
      )}

      {permMember && (
        <PermissionEditor
          member={permMember}
          open={!!permMember}
          onOpenChange={(open) => { if (!open) setPermMember(null); }}
        />
      )}

      <AlertDialog open={!!confirmAction} onOpenChange={(open) => { if (!open) setConfirmAction(null); }}>
        <AlertDialogContent className="rounded-xl">
          <AlertDialogHeader>
            <AlertDialogTitle>
              {confirmAction?.action === 'deactivate'
                ? t('hire.team.usersList.dialog.deactivateTitle')
                : t('hire.team.usersList.dialog.reactivateTitle')}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {confirmAction?.action === 'deactivate'
                ? t('hire.team.usersList.dialog.deactivateDescription', { name: confirmAction.member.full_name })
                : t('hire.team.usersList.dialog.reactivateDescription', { name: confirmAction?.member.full_name })
              }
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-xl">{t('common.cancel')}</AlertDialogCancel>
            <AlertDialogAction
              className={`rounded-xl ${confirmAction?.action === 'deactivate' ? 'bg-destructive text-destructive-foreground hover:bg-destructive/90' : 'bg-primary text-primary-foreground hover:bg-primary/90'}`}
              onClick={() => {
                if (confirmAction) {
                  deactivate.mutate({
                    target_user_id: confirmAction.member.user_id,
                    reactivate: confirmAction.action === 'reactivate',
                  });
                }
                setConfirmAction(null);
              }}
            >
              {confirmAction?.action === 'deactivate'
                ? t('hire.team.usersList.actions.deactivate')
                : t('hire.team.usersList.actions.reactivate')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
